import { Store } from "../../../../../flux/Store";

import { SensorsDataResult, Row, Axis, Reading, DotType, Severity } from "../../../../../rest/Geotab";
import { assertArray, isNumber, hasValue } from "../../../../utils/assert";
import { getTitle } from "../utils";

import { Coordinate } from "./interfaces";
import { names } from "./Actions";

export interface State
{
    view: ViewType;
    refId: string;
    rows: Row[];
    axes: Axis[];
    tableData: TableData;
    hasGeoData: boolean;
    hasChartData: boolean;
    isMapVisible: boolean;
    coordinate: Coordinate;
}

export enum ViewType
{
    Graph,
    TableAndMap
}

export interface TableData
{
    headers: string[];
    rows: TableRow[]; 
}

export interface TableRow
{
    date: Date;
    dateTimeReceivedOn?: Date;
    
    lon?: number;
    lat?: number;
    
    values: TableValue[];
}

export interface TableValue
{
    value: any;
    dotType: DotType;
    severity: Severity;
}

export class SensorsDataStore extends Store<State>
{
    constructor()
    {
        super();
        this.state = this.getDefaultState();

        this.register<SensorsDataResult>(names.SET_SENSORS_DATA, e => this.setSensorsData(e.payload));
        this.register(names.SET_GRAPH_VIEW, e => this.field({ view: ViewType.Graph }));
        this.register(names.SET_TABLE_VIEW, e => this.field({ view: ViewType.TableAndMap }));
        this.register(names.TOGGLE_MAP, e => this.field({ isMapVisible: this.state.hasGeoData && !this.state.isMapVisible }));
        this.register<Coordinate>(names.SET_COORDINATE, e => this.field({ coordinate: e.payload }));
    }

    setSensorsData = ({ Rows, Axes, RefId }: SensorsDataResult): State => {
        assertArray(Rows, 'ChartInfo payload expected');
        fixDate(Rows);
        const tableData = buildTable(Rows);
        const coordinate = getLatestCoordinates(tableData);
        const hasChartData = Rows.some(row => row.Readings.length > 1);
        return this.field({
            refId: RefId,
            rows: Rows,
            axes: Axes,
            tableData: tableData,
            view: hasChartData ? ViewType.Graph : ViewType.TableAndMap,
            coordinate,
            hasGeoData: hasValue(coordinate),
            hasChartData,
        });
    };

    private getDefaultState = (): State => ({
        view: ViewType.Graph,
        refId: null,
        rows: [],
        axes: [],
        tableData: {
            headers: [],
            rows: []
        },
        hasGeoData: false,
        hasChartData: false,
        isMapVisible: false,
        coordinate: null,
    });
}

function fixDate(rows: Row[]): void
{
    rows.forEach(r => r.Readings.forEach(v => {
        v.date = new Date(v.DateTime);
        v.dateTimeReceivedOn = new Date(v.ReceivedOn);
    }));
}

const MAX_DATE = new Date(8640000000000000);
function buildTable(rows: Row[]): TableData
{
    const res: TableData = {
        headers: rows.map(getTitle),
        rows: []
    };
    
    const hasReadings = rows.some(row => row.Readings && row.Readings.length > 0);
    if(!hasReadings)
        return res;
    
    rows.forEach(r => {
        r.Readings.sort((l, r) => l.date.valueOf() - r.date.valueOf());
    });
    
    let cnt = 0;
    const idxs = rows.map(r => 0);
    while(true) {
        let minDate = MAX_DATE;
        rows.forEach((r, i) => {
            if(idxs[i] >= r.Readings.length)
                return;

            const date = r.Readings[idxs[i]].date;
            if(date < minDate)
                minDate = date;
        });
        
        const curRow: TableRow = {date: minDate, values: []};
        res.rows.push(curRow);
        rows.forEach((r, i) => {
            const reading = r.Readings[idxs[i]];
            if(reading && reading.date.valueOf() === minDate.valueOf()) {
                curRow.values.push({ value: reading.Value, dotType: r.DotType, severity: reading.Severity });
                curRow.dateTimeReceivedOn = reading.dateTimeReceivedOn;
                curRow.lon = curRow.lon || reading.Longitude;
                curRow.lat = curRow.lat || reading.Latitude;
                idxs[i] += 1;
            } else {
                curRow.values.push(undefined);
            }
        });

        if(idxs.every((idx, i) => idx >= rows[i].Readings.length))
            break;
    }

    return res;
}

function getLatestCoordinates(tableData: TableData): Coordinate
{
    for(let i = tableData.rows.length - 1; i >= 0; i--) {
        const { lon, lat } = tableData.rows[i];
        if(isNumber(lon) && isNumber(lat)) {
            return { lon, lat };
        }
    }
    return null;
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/SensorsData/flux/Store.ts